import React from "react";
import { Table, Sheet } from "@mui/joy";
import { MoreHorizIcon } from "../../../styles/StyledMUI";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import CommentsModal from "../../common/CommentsModal";
import type { LeaveType, UserLeaveTypeBalance } from "../../../api/leave/Leave.typed";
import { hrSheetSx, hrTableSx } from "../../../styles/globalStyles";

export type LeaveValidationBalancesBlockProps = {
  balances: UserLeaveTypeBalance[];
  leaveTypes: LeaveType[];
  openCommentsForType: number | null;
  openComments: Record<number, UserLeaveTypeBalance["MultiComments"]>;
  setOpenCommentsForType: (typeId: number | null) => void;
  setOpenComments: React.Dispatch<
    React.SetStateAction<Record<number, UserLeaveTypeBalance["MultiComments"]>>
  >;
  onSaveComments: (
    typeId: number,
    userId: number,
    updated: UserLeaveTypeBalance["MultiComments"],
  ) => Promise<void>;
};

export default function LeaveValidationBalancesBlock({
  balances,
  leaveTypes,
  openCommentsForType,
  openComments,
  setOpenCommentsForType,
  setOpenComments,
  onSaveComments,
}: LeaveValidationBalancesBlockProps) {
  return (
    <Sheet sx={{ mt: 2, ...hrSheetSx }}>
      <Table size="sm" sx={hrTableSx}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Solde</th>
            <th>MAJ</th>
            <th>Commentaires</th>
          </tr>
        </thead>
        <tbody>
          {balances.map((b) => (
            <React.Fragment key={`${b.TypeID}-${b.UserID}`}>
              <tr>
                <td>
                  <DarkTypography level="body-sm">
                    {leaveTypes.find((t) => t.ID === b.TypeID)?.Name ?? `Type #${b.TypeID}`}
                  </DarkTypography>
                </td>
                <td>
                  <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {b.Balance}
                  </DarkTypography>
                </td>
                <td>
                  <DarkTypography level="body-xs" colorVariant="muted">
                    {new Date(b.UpdatedAt).toLocaleDateString("fr-FR")}
                  </DarkTypography>
                </td>
                <td>
                  <IconButtonCTA
                    aria-label="Ouvrir les commentaires du compteur"
                    ctaVariant="accent"
                    size="sm"
                    onClick={() =>
                      setOpenCommentsForType(openCommentsForType === b.TypeID ? null : b.TypeID)
                    }
                  >
                    <MoreHorizIcon />
                  </IconButtonCTA>
                </td>
              </tr>
              {openCommentsForType === b.TypeID && (
                <CommentsModal
                  open
                  title={`Commentaires - ${leaveTypes.find((t) => t.ID === b.TypeID)?.Name ?? b.TypeID}`}
                  currentUserName={undefined}
                  multiComments={openComments[b.TypeID]}
                  setMultiComments={(mc) =>
                    setOpenComments((prev) => {
                      const current = prev[b.TypeID];
                      const next = typeof mc === "function" ? mc(current) : mc;
                      return { ...prev, [b.TypeID]: next };
                    })
                  }
                  onClose={() => setOpenCommentsForType(null)}
                  onSave={async (updated) => {
                    await onSaveComments(b.TypeID, b.UserID, updated ?? []);
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}
